import React from 'react';
import { Field, ErrorMessage } from 'formik';
import { Radio, Select, Checkbox, Divider, Space, Row, Col, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { StyledSelect as SharedStyledSelect } from '../../shared/StyledSelect';
import { US_STATES, formatPhoneNumber } from '../constants';
import { RACE_OPTIONS } from '../../../constants/raceEthnicity';
import {
  colors,
  StepTitle,
  InfoBox,
  FormSection,
  FormItem,
  SectionTitle,
  ErrorText,
  LargeInput,
  LargeCheckbox,
  TopAlignedCheckbox,
  LargeRadio,
  inlineStyles
} from '../styled';

const { Paragraph, Text } = Typography;
const { Option } = Select;

const StepDemographics = ({ values, errors, touched, setFieldValue, isFieldVisible = () => true }) => {
  const hasDemographicsFields = isFieldVisible('birthGender') || isFieldVisible('raceEthnicity') || isFieldVisible('householdIncome') || isFieldVisible('householdSize') || isFieldVisible('insuranceCoverage') || isFieldVisible('doctorHelpInterest');
  const hasLocationFields = isFieldVisible('stateOfResidence') || isFieldVisible('eventZip');

  return (
  <>
    <StepTitle level={4}>Contact & Demographics</StepTitle>
    <InfoBox>
      <div className="icon"><InfoCircleOutlined /></div>
      <div className="content">
        <Paragraph style={inlineStyles.infoParagraph}>
          Your contact and demographic information helps us understand the communities we serve and improve our healthcare services.
        </Paragraph>
      </div>
    </InfoBox>

    {isFieldVisible('phoneNumber') && (
    <FormSection>
      <FormItem>
        <SectionTitle>Phone Number</SectionTitle>
        <Field name="phoneNumber">
          {({ field }) => (
            <LargeInput
              {...field}
              placeholder="(XXX) XXX-XXXX"
              style={inlineStyles.errorBorderInput(touched.phoneNumber, errors.phoneNumber)}
              onChange={(e) => {
                const formattedPhoneNumber = formatPhoneNumber(e.target.value);
                setFieldValue('phoneNumber', formattedPhoneNumber);
              }}
            />
          )}
        </Field>
        <ErrorMessage name="phoneNumber" component={ErrorText} />
      </FormItem>

      {isFieldVisible('transactionalSmsConsent') && (
      <>
      <Divider style={{ margin: '32px 0' }} />
      <FormItem>
        <SectionTitle>SMS Consent Preferences</SectionTitle>
        <div style={{ marginTop: '12px' }}>
          <div style={{ marginBottom: '16px' }}>
            <TopAlignedCheckbox
              checked={!!values.transactionalSmsConsent}
              onChange={(e) => setFieldValue('transactionalSmsConsent', e.target.checked)}
            >
              <span style={{ fontSize: '13px' }}>By checking, you agree to receive transactional/informational SMS communications regarding appointment confirmations, reminders, scheduling updates, and customer care from Black Heart Association. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out. Reply HELP for help.</span>
            </TopAlignedCheckbox>
          </div>

          <Paragraph style={{ fontSize: '14px', color: colors.text, marginTop: '12px', marginBottom: 0 }}>
            By providing your number, you agree to our <a href="https://blackheartassociation.org/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Terms</a> and acknowledge our <a href="https://blackheartassociation.org/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Privacy</a> Policy.
          </Paragraph>
        </div>
      </FormItem>
      </>
      )}
    </FormSection>
    )}

    {hasDemographicsFields && (
    <>
    <Divider style={inlineStyles.dividerStyle}>
      <Text style={inlineStyles.dividerText}>Demographics</Text>
    </Divider>

    <FormSection>
      {isFieldVisible('birthGender') && (
      <FormItem>
        <SectionTitle>Birth Gender</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Radio.Group
            value={values.birthGender}
            onChange={(e) => setFieldValue('birthGender', e.target.value)}
          >
            <Space direction="horizontal" size="large" wrap>
              <LargeRadio value="male">Male</LargeRadio>
              <LargeRadio value="female">Female</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="birthGender" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('raceEthnicity') && (
      <FormItem>
        <SectionTitle>Race/Ethnicity</SectionTitle>
        <SharedStyledSelect
          style={inlineStyles.selectStyle(touched.raceEthnicity, errors.raceEthnicity)}
          placeholder="Choose Your Race/Ethnicity"
          value={values.raceEthnicity}
          onChange={(value) => setFieldValue('raceEthnicity', value)}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          {RACE_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
          ))}
        </SharedStyledSelect>
        <ErrorMessage name="raceEthnicity" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('householdIncome') && (
      <FormItem>
        <SectionTitle>What is your estimated yearly household income?</SectionTitle>
        <Paragraph style={{ fontSize: '14px', color: colors.text, marginTop: '4px', marginBottom: '12px' }}>
          This information helps us better serve our community.
        </Paragraph>
        <div style={inlineStyles.topMarginDiv}>
          <Checkbox.Group
            value={values.householdIncome ? [values.householdIncome] : []}
            onChange={(checkedValues) => setFieldValue('householdIncome', checkedValues[checkedValues.length - 1] || '')}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <LargeCheckbox value="Less than $15,000">Less than $15,000</LargeCheckbox>
              </Col>
              <Col span={12}>
                <LargeCheckbox value="$15,000 - $24,999">$15,000 – $24,999</LargeCheckbox>
              </Col>
              <Col span={12}>
                <LargeCheckbox value="$25,000 - $34,999">$25,000 – $34,999</LargeCheckbox>
              </Col>
              <Col span={12}>
                <LargeCheckbox value="$35,000 - $49,999">$35,000 – $49,999</LargeCheckbox>
              </Col>
              <Col span={12}>
                <LargeCheckbox value="$50,000 - $74,999">$50,000 – $74,999</LargeCheckbox>
              </Col>
              <Col span={12}>
                <LargeCheckbox value="$75,000 - $99,999">$75,000 – $99,999</LargeCheckbox>
              </Col>
              <Col span={12}>
                <LargeCheckbox value="$100,000 or more">$100,000 or more</LargeCheckbox>
              </Col>
              <Col span={12}>
                <LargeCheckbox value="Prefer not to say">Prefer not to say</LargeCheckbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </div>
        <ErrorMessage name="householdIncome" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('householdSize') && (
      <FormItem>
        <SectionTitle>How many people live in your household (including you)?</SectionTitle>
        <SharedStyledSelect
          style={inlineStyles.selectStyle(touched.householdSize, errors.householdSize)}
          placeholder="Please Select"
          value={values.householdSize}
          onChange={(value) => setFieldValue('householdSize', value)}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Option value="1">1</Option>
          <Option value="2">2</Option>
          <Option value="3">3</Option>
          <Option value="4">4</Option>
          <Option value="5">5</Option>
          <Option value="6">6</Option>
          <Option value="7">7</Option>
          <Option value="8">8</Option>
          <Option value="9">9</Option>
          <Option value="10+">10+</Option>
        </SharedStyledSelect>
        <ErrorMessage name="householdSize" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('insuranceCoverage') && (
      <FormItem>
        <SectionTitle>Current Insurance Coverage</SectionTitle>
        <SharedStyledSelect
          style={inlineStyles.selectStyle(touched.insuranceCoverage, errors.insuranceCoverage)}
          placeholder="Choose Your Insurance Coverage"
          value={values.insuranceCoverage}
          onChange={(value) => setFieldValue('insuranceCoverage', value)}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Option value="private">Private</Option>
          <Option value="insuredThroughEmployer">Insured through employer</Option>
          <Option value="medicaid">Medicaid</Option>
          <Option value="medicare">Medicare</Option>
          <Option value="military">Military</Option>
          <Option value="noCoverage">No coverage</Option>
        </SharedStyledSelect>
        <ErrorMessage name="insuranceCoverage" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('doctorHelpInterest') && (
      <FormItem>
        <SectionTitle>Would you like help finding a doctor?</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Radio.Group
            value={values.doctorHelpInterest}
            onChange={(e) => setFieldValue('doctorHelpInterest', e.target.value)}
          >
            <Space direction="horizontal" size="large" wrap>
              <LargeRadio value="yes">Yes</LargeRadio>
              <LargeRadio value="no">No</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="doctorHelpInterest" component={ErrorText} />
      </FormItem>
      )}
    </FormSection>
    </>
    )}

    {hasLocationFields && (
    <>
    <Divider style={inlineStyles.dividerStyle}>
      <Text style={inlineStyles.dividerText}>Location</Text>
    </Divider>

    <FormSection>
      {isFieldVisible('stateOfResidence') && (
      <FormItem>
        <SectionTitle>State of Residence</SectionTitle>
        <SharedStyledSelect
          style={inlineStyles.selectStyle(touched.stateOfResidence, errors.stateOfResidence)}
          placeholder="Choose Your State"
          value={values.stateOfResidence}
          onChange={(value) => setFieldValue('stateOfResidence', value)}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
          }
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          {US_STATES.map((state) => (
            <Option key={state.value} value={state.value}>{state.label}</Option>
          ))}
        </SharedStyledSelect>
        <ErrorMessage name="stateOfResidence" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('eventZip') && (
      <FormItem>
        <SectionTitle>Zip Code</SectionTitle>
        <Field name="eventZip">
          {({ field }) => (
            <LargeInput
              {...field}
              placeholder="Enter your zip code"
              maxLength={5}
              style={inlineStyles.errorBorderInput(touched.eventZip, errors.eventZip)}
            />
          )}
        </Field>
        <ErrorMessage name="eventZip" component={ErrorText} />
      </FormItem>
      )}
    </FormSection>
    </>
    )}
  </>
  );
};

export default StepDemographics;
